import { AxiosError } from "axios";
import { ResumeData } from "./parse";
import { Student } from "../types";
import apiClient, { isApiError, ApiError } from "./api"; // Import the shared apiClient
import { getUserByEmail } from "./user";
import { IS_DEV } from '../utils/misc';

interface UpdateResponse {
  message: string;
}

/**
 * Fetches a user's profile data from the backend using their user ID.
 * Uses the /api/profile/{userId} endpoint.
 * @param userId The ID of the user whose profile is to be fetched.
 * @returns A promise resolving to the user's ResumeData or null if not found.
 */
export const getProfile = async (email: string): Promise<ResumeData | null> => {
  if (IS_DEV) console.log("[profile.ts] getProfile called with email:", email);

  try {
    const user = await getUserByEmail(email);
    if (!user || !user.id) {
      console.error("No user ID found in session");
      return null;
    }

    const response = await apiClient.get<ResumeData>(`/api/profile/${user.id}`);
    console.log("[profile.ts] getProfile successful:", response.data);
    return response.data;
  } catch (error: unknown) {
    console.error("[profile.ts] Error fetching profile:", error);

    if (
      isApiError(error) &&
      (error as any).response &&
      (error as any).response.status === 404
    ) {
      console.log("[profile.ts] Profile not found (404), returning null.");
      return null;
    }
    if (error instanceof AxiosError) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response && axiosError.response.data) {
        console.error(
          "Error fetching profile:",
          axiosError.response.data.message || axiosError.message
        );
        throw axiosError.response.data;
      } else {
        console.error(
          "Axios error (no response or no data field):",
          axiosError.message
        );
        throw new Error(
          `Network error fetching profile: ${axiosError.message}`
        );
      }
    } else if (error instanceof Error) {
      console.error("An unexpected error occurred:", error.message);
      throw new Error(
        `An unexpected error occurred while fetching profile: ${error.message}`
      );
    } else {
      console.error("An unexpected non-error was thrown:", error);
      throw new Error(
        "An unexpected non-error occurred while fetching profile."
      );
    }
  }
};

export const getProfileWithElo = async (
  email: string
): Promise<{ profile: ResumeData | null; elo: number | undefined; }> => {
  const profile = await getProfile(email);
  const user = await getUserByEmail(email);
  return { profile, elo: user?.elo };
};

/**
 * Updates the logged-in user's profile data on the backend.
 * Original: POST to /api/profile/update
 * @param profileData The ResumeData to save.
 * @returns A promise resolving to the UpdateResponse (or original type).
 */
export const updateProfile = async (
  profileData: ResumeData,
  user_email: string
): Promise<UpdateResponse> => {
  console.log(
    "[profile.ts] updateProfile called with data:",
    profileData,
    "for email:",
    user_email
  );
  try {
    const dataWithEmail = {
      ...profileData,
      user_email: user_email,
    };
    const response = await apiClient.post<UpdateResponse>(
      "/api/profile/update",
      dataWithEmail
    );
    console.log("[profile.ts] updateProfile successful:", response.data);
    return response.data;
  } catch (error: unknown) {
    console.error("[profile.ts] updateProfile failed:", error);
    if (error instanceof AxiosError) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response && axiosError.response.data) {
        console.error(
          "Error updating profile:",
          axiosError.response.data.message || axiosError.message
        );
        throw axiosError.response.data;
      } else {
        console.error(
          "Axios error (no response or no data field):",
          axiosError.message
        );
        throw new Error(
          `Network error updating profile: ${axiosError.message}`
        );
      }
    } else if (error instanceof Error) {
      console.error("An unexpected error occurred:", error.message);
      throw new Error(
        `An unexpected error occurred while updating profile: ${error.message}`
      );
    } else {
      console.error("An unexpected non-error was thrown:", error);
      throw new Error(
        "An unexpected non-error occurred while updating profile."
      );
    }
  }
};

/**
 * Fetches a specific user's full profile by their ID.
 * Uses the /users/profile/<userId> endpoint.
 * @param userId - The ID of the user whose profile is to be fetched.
 * @returns A promise resolving to the user's full Student profile or null if not found.
 */
export const getUserProfileById = async (
  userId: number
): Promise<Student | null> => {
  console.log(`[profile.ts] getUserProfileById called for userId: ${userId}`);
  const url = `/users/profile/${userId}`;
  try {
    const response = await apiClient.get<Student>(url);
    console.log(
      `[profile.ts] getUserProfileById successful for ${userId}:`,
      response.data
    );
    return response.data;
  } catch (error: unknown) {
    console.error(
      `[profile.ts] getUserProfileById failed for ${userId}:`,
      error
    );
    if (
      isApiError(error) &&
      (error as any).response &&
      (error as any).response.status === 404
    ) {
      console.log(
        `[profile.ts] User profile not found (404) for userId: ${userId}`
      );
      return null;
    }

    if (error instanceof AxiosError) {
      const axiosError = error as AxiosError<ApiError>;

      if (axiosError.response) {
        console.error(
          `Error fetching profile for ${userId}:`,
          axiosError.response.data
        );
        throw axiosError.response.data;
      } else {
        console.error("Axios error (no response):", axiosError.message);
        throw new Error(
          `Network error fetching profile for ${userId}: ${axiosError.message}`
        );
      }
    } else if (error instanceof Error) {
      console.error("An unexpected error occurred:", error.message);
      throw new Error(
        `An unexpected error occurred fetching profile for ${userId}: ${error.message}`
      );
    } else {
      console.error("An unexpected non-error was thrown:", error);
      throw new Error(
        `An unexpected non-error occurred fetching profile for ${userId}.`
      );
    }
  }
};

// Remove the local isApiError if it's now imported from api.ts
// export const isApiError = (error: any): error is ApiError => {
//   return error && typeof error === "object" && "message" in error;
// };
